/**
 * HeroSection Component
 * Hero/banner section for the public landing page
 */

import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { useNavigate } from "react-router-dom";
import { tokenStorage } from "../../config/api";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useGetPublicPropertiesQuery } from "../../store/api/publicPropertiesApi";
import Icon from "../AppIcon";
import Button from "../ui/Button";

interface HeroSectionProps {
	/**
	 * Optional callback to scroll to properties section
	 */
	onViewProperties?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onViewProperties }) => {
	const navigate = useNavigate();

	// Check if user is logged in
	const accessToken = tokenStorage.getAccessToken();
	const { data: currentUser } = useGetProfileQuery(
		accessToken ? undefined : skipToken,
	);

	// Fetch property count for real-time stats (don't block rendering on error)
	const {
		data: propertiesData,
		isLoading,
		isError,
	} = useGetPublicPropertiesQuery({
		page_size: 1,
	});

	const handleViewProperties = () => {
		if (onViewProperties) {
			onViewProperties();
		}
	};

	// Get dashboard route based on user role
	const getDashboardRoute = () => {
		if (!currentUser) return "/";
		switch (currentUser.role) {
			case "admin":
				return "/admin-dashboard";
			case "tenant":
				return "/tenant-dashboard";
			case "technician":
				return "/technician-dashboard";
			case "agent":
				return "/agent-dashboard";
			default:
				return "/";
		}
	};

	return (
		<section className="relative py-20 sm:py-28 overflow-hidden min-h-[600px] sm:min-h-[700px] flex items-center">
			{/* Background Image - will be set via CSS variable or inline style */}
			<div
				className="absolute inset-0 bg-cover bg-center bg-no-repeat"
				style={{
					backgroundImage:
						'var(--hero-background-image, url("/hero-background.jpg"))',
				}}
			>
				{/* Overlay for better text readability */}
				<div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"></div>
			</div>

			{/* Decorative Background Elements */}
			<div className="absolute inset-0 overflow-hidden">
				<div className="absolute -top-1/2 -right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
				<div className="absolute -bottom-1/2 -left-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl"></div>
			</div>

			<div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
					{/* Left Column - Text Content */}
					<div className="text-center lg:text-left relative z-10">
						<h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 drop-shadow-lg">
							Find Your Next Home with{" "}
							<span className="text-primary-foreground">Dofarahub</span>
						</h1>

						<p className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto lg:mx-0 drop-shadow-md">
							Discover trusted properties from verified Owners. Easy tenant
							onboarding, secure payments, and seamless property management -
							all in one platform.
						</p>

						{/* CTA Buttons */}
						<div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
							<Button
								size="lg"
								onClick={handleViewProperties}
								className="text-base group"
							>
								Browse Properties
							</Button>

							{currentUser ? (
								<Button
									size="lg"
									variant="outline"
									onClick={() => navigate(getDashboardRoute())}
									className="text-base"
								>
									<Icon name="Home" size={20} className="mr-2" />
									Go to Dashboard
								</Button>
							) : (
								<Button
									size="lg"
									variant="outline"
									onClick={() => navigate("/register")}
									className="text-base"
								>
									Sign Up
								</Button>
							)}
						</div>
					</div>
				</div>
			</div>

			{/* Add animate-float to global CSS if not present */}
			<style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
		</section>
	);
};

export default HeroSection;
